import Debug from 'debug'
import { action, computed, makeObservable, observable, reaction } from 'mobx'
import type { ChangeEvent } from 'react'
import { createRef } from 'react'
import type { Subscription } from 'rxjs'

import KeyboardListController from '@src/component/keyboard-list/KeyboardListController'
import { DisposeBag } from '@src/lib/dispose'
import isNonNull from '@src/lib/isNonNull'
import {
  emergencyNumbers,
  formatPartial,
  isValidNumber,
  toE164,
} from '@src/lib/phone-number'
import shortId from '@src/lib/shortId'
import Stepper from '@src/lib/stepper'
import { compareString } from '@src/lib/string'
import Collection from '@src/service/collections/Collection'
import type {
  Identity,
  IdentityPhone,
  MemberModel,
  PhoneNumberGroup,
  PhoneNumberModel,
} from '@src/service/model'
import { compareByName, createAnonymousIdentityFromNumber } from '@src/service/model'
import type { ContactModel } from '@src/service/model/contact/ContactModel'

export const getTypeLabel = (type: IdentityRowInstanceType) => {
  return type === 'contact'
    ? 'Contact'
    : type === 'inbox'
    ? 'Inbox'
    : type === 'inbox-member'
    ? 'Member'
    : type === 'member'
    ? 'Member'
    : 'User'
}

export type PhoneNumberSelection =
  | {
      type: 'inbox'
      to: PhoneNumberModel
    }
  | {
      type: 'inbox-member'
      to: MemberModel
      /**
       * Direct number or phone number
       */
      via: string
      // FIXME refactor this
      id?: string
    }
  | {
      type: 'member'
      to: MemberModel
      /**
       * Direct number or phone number
       */
      via: string

      // FIXME refactor this
      id?: string
    }
  | {
      type: 'contact'
      to: ContactModel

      /**
       * The E164 formatted phone number of the contact.
       */
      via: string
    }
  | {
      type: 'number'

      /**
       * The E164 formatted phone number.
       */
      to: string
    }
  | {
      type: 'add-number'

      /**
       * The E164 formatted phone number.
       */
      to: string
    }

type DatedPhoneNumberSelection = PhoneNumberSelection & { at: Date }

type PhoneNumberSelectorControllerLang = {
  SELECT_CUSTOM: string
  PLACEHOLDER: string
}

export default abstract class PhoneNumberSelectorController {
  abstract currentUser: MemberModel | null
  abstract currentPhoneNumber: PhoneNumberModel | null
  abstract currentPhoneNumberMembers: readonly MemberModel[]
  abstract phoneNumbers: readonly PhoneNumberModel[]
  abstract members: readonly MemberModel[]
  abstract contacts: readonly ContactModel[]
  abstract exclusions: readonly Identity[]

  readonly id = shortId()
  readonly debug = Debug(`op:phone-number-selector:@${this.id}`)

  protected rows: Collection<Row> = new Collection<Row>()
  protected readonly filteredRows = new Collection<Row>({
    filter: this.filterRow.bind(this),
    compare: this.compareRows.bind(this),
  })
  protected filteredSubscription?: Subscription
  protected disposeBag: DisposeBag

  readonly keyboardList = new PhoneNumberSelectorKeyboardListController(this)
  readonly inputRef = createRef<HTMLInputElement>()
  readonly mode: 'single' | 'multiple' = 'multiple'
  readonly size: 24 | 30 = 24
  readonly lang: PhoneNumberSelectorControllerLang = {
    SELECT_CUSTOM: 'Select',
    PLACEHOLDER: 'Members, inboxes, contacts, or numbers...',
  }
  readonly rowTypesOrderMap = {
    [AddNumberRow.type]: 1,
    [NumberRow.type]: 2,
    [ContactRow.type]: 3,
    [InboxMemberRow.type]: 4,
    [InboxRow.type]: 5,
    [MemberRow.type]: 6,
  }

  search = ''
  focused = false
  activeRow: Row | null = null

  constructor() {
    this.debug('new controller')
    this.keyboardList = new PhoneNumberSelectorKeyboardListController(this)

    makeObservable<this, 'reset'>(this, {
      activeRow: observable.ref,
      focused: observable.ref,
      search: observable.ref,
      selectedRows: computed,
      selections: computed,
      phoneNumberGroups: computed,
      hasResults: computed,
      focusedNode: computed,
      handleSelect: action.bound,
      handleSelectRow: action.bound,
      handleSearchChange: action.bound,
      handleSearchClear: action.bound,
      handleSearchFocus: action.bound,
      handleSearchBlur: action.bound,
      clearSearch: action.bound,
      clearSelection: action.bound,
      clearSelections: action.bound,
      clearRowSelection: action.bound,
      openMenu: action.bound,
      closeMenu: action.bound,
      reset: action.bound,
    })

    this.disposeBag = new DisposeBag(
      reaction(
        () => this.search,
        () => {
          if (this.mode === 'single') {
            this.clearSelections()
          }
          this.bindCollections()
          setTimeout(() => {
            this.keyboardList.stepper.start()
            this.keyboardList.handleRef.current?.scrollIntoView({ index: 0 })
          }, 200)
        },
        { name: 'PhoneNumberSelector.SearchChanged', fireImmediately: true },
      ),
      reaction(
        () => this.selectedRows,
        () => {
          this.bindCollections()
          setTimeout(() => this.keyboardList.stepper.start(), 200)
        },
        { name: 'PhoneNumberSelector.SelectedRowsChanged', fireImmediately: true },
      ),
      reaction(
        () => this.contacts,
        () => {
          this.repopulateIdentityRowsOfType('contact')
        },
        { name: 'PhoneNumberSelector.ContactsChanged' },
      ),
    )
  }

  get data() {
    return this.filteredRows
  }

  get selectedRows(): IdentityRowInstance[] {
    return this.rows.list
      .filter((row): row is IdentityRowInstance => !!row.selection)
      .sort((a, b) => (a.selection?.at.getTime() ?? 0) - (b.selection?.at.getTime() ?? 0))
  }

  get selections(): PhoneNumberSelection[] {
    return this.selectedRows.map((row) => row.selection).filter(isNonNull)
  }

  get phoneNumberGroups(): PhoneNumberGroup[] {
    return this.phoneNumbers.map((phoneNumber) => phoneNumber.group).filter(isNonNull)
  }

  get hasResults(): boolean {
    return Boolean(this.data.find((item) => item.type !== HeaderRow.type))
  }

  get focusedNode(): Element | Document {
    return this.focused ? this.inputRef.current ?? document : document
  }

  /**
   * Called when a phone number is selected.
   */
  handleSelect(selection: PhoneNumberSelection): void {
    this.debug('handling select (selection: %O)', selection)

    this.closeMenu()

    const row = this.rows.find((r) => r.matches(selection))

    if (!row) {
      this.debug('no row found via selection (selection: %O)', selection)
      return
    }

    this.clearRowSelection(row)

    if (this.mode === 'single') {
      this.clearSelections()
    } else {
      this.clearSearch()
    }

    row.selection = { ...selection, at: new Date() }
  }
  /**
   * Called when a row is selected.
   */
  handleSelectRow(row: Row) {
    if (row instanceof IdentityRow && row.rowMeta.get(row.id)?.disabled) {
      return
    }

    this.debug('row selected (row: %O)', row)

    switch (row.type) {
      case MemberRow.type:
        // FIXME: this is hack due to the current nerf of direct dialing
        return (this.mode === 'single' || this.mode === 'multiple') &&
          row.phones.length > 1
          ? this.openMenu(row)
          : this.handleSelect({
              type: row.type,
              to: row.identity,
              // @ts-expect-error unchecked index access
              id: row.phones[0].id ?? undefined,
              via:
                // FIXME: this is hack due to the current nerf of direct dialing
                this.mode === 'single' || this.mode === 'multiple'
                  ? row.phones[0]?.number ?? ''
                  : row.identity.directNumber?.number ?? '',
            })
      case ContactRow.type:
        return row.phones.length > 1
          ? this.openMenu(row)
          : this.handleSelect({
              type: row.type,
              to: row.identity,
              via: row.phones[0]?.number ?? '',
            })
      case InboxRow.type:
        return this.handleSelect({ type: 'inbox', to: row.phoneNumber })
      case InboxMemberRow.type:
        return this.handleSelect({
          type: 'inbox-member',
          to: row.member,
          id: row.phoneNumber.id,
          via:
            // FIXME: this is hack due to the current nerf of direct dialing
            this.mode === 'single' || this.mode === 'multiple'
              ? row.phoneNumber.number
              : row.identity.directNumber?.number ?? '',
        })
      case NumberRow.type:
        return this.handleSelect({ type: 'number', to: row.phones[0]?.number ?? '' })
      case AddNumberRow.type:
        if (this.mode === 'single') {
          return this.handleSelect({ type: 'add-number', to: row.formattedNumber ?? '' })
        } else {
          this.clearSearch()
          return this.rows.put(
            new NumberRow(this, {
              type: 'number',
              to: row.formattedNumber ?? '',
              at: new Date(),
            }),
          )
        }
    }
  }

  handleSearchChange(event: ChangeEvent<HTMLInputElement>) {
    this.search = event.target.value
  }

  handleSearchClear() {
    this.clearSearch()
  }

  handleSearchFocus() {
    this.focused = true
  }

  handleSearchBlur() {
    this.focused = false
  }

  clearSearch() {
    this.search = ''
  }

  clearSelection(selection: PhoneNumberSelection) {
    const row = this.rows.list.find((r) => r.matches(selection))

    if (!row) {
      this.debug('no row found via selection (selection: %O)', selection)
      return
    }

    this.clearRowSelection(row)
  }

  clearSelections() {
    this.rows.list.forEach(this.clearRowSelection)
  }

  clearRowSelection(row: Row) {
    if (!row.selection) {
      return
    }

    if (row.type === 'number') {
      this.rows.delete(row)
      return
    }

    row.selection = null
  }

  openMenu(row: Row) {
    this.activeRow = row
  }

  closeMenu() {
    this.activeRow = null
  }

  /**
   * Modify an IdentityRow during creation.
   *
   * Use this to change various display or meta information about each row.
   */
  augmentIdentityRow?(row: IdentityRow): void

  /**
   * Bind the collection to the rows
   */
  protected bindCollections() {
    if (this.rows.length === 0) {
      this.replaceRows()
    }

    const addNumberRow = this.rows.find(AddNumberRow.is)
    if (addNumberRow) {
      if (addNumberRow.formattedNumber !== addNumberRow.selection?.to) {
        addNumberRow.selection = null
      }
      addNumberRow.rawNumber = null
      if (this.search) {
        addNumberRow.rawNumber = this.search
      }
      this.augmentIdentityRow?.(addNumberRow)
    }

    this.filteredSubscription?.unsubscribe()
    this.filteredSubscription = this.filteredRows.bind(this.rows)
  }

  /**
   * Populate the rows from the data arrays and create the header rows.
   */
  protected replaceRows() {
    const selectedRows = this.selectedRows
    this.rows.replace([
      this.instantiateHeaderRow(AddNumberRow.type),
      new AddNumberRow(this),
      this.instantiateHeaderRow(ContactRow.type),
      ...this.contacts.map((contact) =>
        this.instantiateIdentityRow(ContactRow.type, contact),
      ),
      this.instantiateHeaderRow(InboxMemberRow.type),
      ...this.currentPhoneNumberMembers.map((member) =>
        this.instantiateIdentityRow(InboxMemberRow.type, member),
      ),
      this.instantiateHeaderRow(MemberRow.type),
      ...this.members.map((member) =>
        this.instantiateIdentityRow(MemberRow.type, member),
      ),
      this.instantiateHeaderRow(InboxRow.type),
      ...this.phoneNumberGroups.map((group) =>
        this.instantiateIdentityRow(InboxRow.type, group),
      ),
    ])

    for (const oldRow of selectedRows) {
      const newRow = this.rows.get(oldRow.id) as IdentityRowInstance | null

      if (newRow) {
        newRow.selection = oldRow.selection
      }
    }

    this.filteredRows.refilter()
  }

  /**
   * Create the header row by type.
   */
  protected instantiateHeaderRow(type: IdentityRowInstanceType) {
    switch (type) {
      case AddNumberRow.type:
        return new HeaderRow(this, AddNumberRow.type, 'Direct number')
      case NumberRow.type:
        return new HeaderRow(this, NumberRow.type, 'Number')
      case InboxMemberRow.type:
        return new HeaderRow(
          this,
          InboxMemberRow.type,
          `Others in ${this.currentPhoneNumber?.formattedName}`,
        )
      case InboxRow.type:
        return new HeaderRow(this, type, 'Shared inboxes')
      case MemberRow.type:
        return new HeaderRow(this, MemberRow.type, 'Members')
      case ContactRow.type:
        return new HeaderRow(this, ContactRow.type, 'Contacts')
    }
  }

  /**
   * Create the indentity row by type.
   */
  protected instantiateIdentityRow(
    type: Exclude<IdentityRowInstanceType, 'number'>,
    // TODO: could remove this any by using a tuple instead of parameters
    identity: any,
  ): Exclude<IdentityRowInstance, NumberRow> {
    let row: Exclude<IdentityRowInstance, NumberRow>

    switch (type) {
      case AddNumberRow.type:
        row = new AddNumberRow(this)
        break
      case InboxMemberRow.type:
        row = new InboxMemberRow(
          this,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- FIXME: Fix this ESLint violation!
          identity,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access -- FIXME: Fix this ESLint violation!
          this.currentPhoneNumber ?? identity.phoneNumber,
        )
        break
      case InboxRow.type:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access -- FIXME: Fix this ESLint violation!
        row = new InboxRow(this, identity.phoneNumber)
        break
      case MemberRow.type:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- FIXME: Fix this ESLint violation!
        row = new MemberRow(this, identity)
        break
      case ContactRow.type:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- FIXME: Fix this ESLint violation!
        row = new ContactRow(this, identity)
        break
    }

    this.augmentIdentityRow?.(row)

    return row
  }

  /**
   * Bulk delete and update rows to update the list.
   */
  protected repopulateIdentityRowsOfType(
    type: Exclude<IdentityRowInstanceType, 'add-number' | 'number'>,
  ) {
    const identities: readonly Identity[] =
      type === 'inbox-member'
        ? this.currentPhoneNumberMembers
        : type === 'inbox'
        ? this.phoneNumberGroups
        : type === 'member'
        ? this.members
        : this.contacts

    const cls = IdentityRow.getRowClass(type) as Exclude<
      IdentityRowClass,
      typeof NumberRow | typeof AddNumberRow
    >

    const oldHeaderRow = this.rows.list.find(
      (r) => HeaderRow.is(r) && r.rowType === cls.type,
    )
    const newHeaderRow = this.instantiateHeaderRow(cls.type)

    const oldRows = this.rows.list.filter((r) => cls.is(r) && !r.selection)
    const newRows = identities.map((identity) =>
      this.instantiateIdentityRow(cls.type, identity),
    )

    this.rows.deleteBulk(oldRows)
    if (oldHeaderRow) {
      this.rows.delete(oldHeaderRow)
    }
    this.rows.putBulk(newRows)
    this.rows.put(newHeaderRow)
  }

  protected reset() {
    this.keyboardList.stepper.selectIndex(0)
    const numberRows = this.rows.list.filter((r) => NumberRow.is(r))
    this.rows.deleteBulk(numberRows)
    this.clearSelections()
    this.clearSearch()
    this.activeRow = null
  }

  // protected setSearch = debounce(
  //   action('setSearch', (search: string | null) => {
  //     this.search = search
  //     this.keyboardList.stepper.selectIndex(0)
  //   }),
  //   500,
  // )

  private compareRows(a: Row, b: Row): number {
    // The order of the chained sorting functions matters
    // meaning that we want to sort first by the sort weight,
    // then by the online status, and so on.
    //
    // In case of needing to add another sorting function make
    // sure to return null if the criteria for the sorted items
    // is not met so that the next sorting function is executed.
    return (
      this.compareRowsBySortWeight(a, b) ??
      this.compareInboxMemberRowsByOnlineStatus(a, b) ??
      this.compareInboxRowsByOffHours(a, b) ??
      this.compareIdentityRowsByName(a, b) ??
      this.compareRowsById(a, b)
    )
  }

  private compareRowsBySortWeight(a: Row, b: Row): number | null {
    const sortWeightA =
      a.type === 'header'
        ? this.rowTypesOrderMap[a.rowType] - 0.5
        : this.rowTypesOrderMap[a.type]
    const sortWeightB =
      b.type === 'header'
        ? this.rowTypesOrderMap[b.rowType] - 0.5
        : this.rowTypesOrderMap[b.type]
    const diff = sortWeightA - sortWeightB

    if (diff === 0) {
      return null
    }

    return diff
  }

  private compareInboxMemberRowsByOnlineStatus(a: Row, b: Row): number | null {
    if (a instanceof InboxMemberRow && b instanceof InboxMemberRow) {
      if (a.member.presence?.isOnline && !b.member.presence?.isOnline) {
        return -1
      }

      if (!a.member.presence?.isOnline && b.member.presence?.isOnline) {
        return 1
      }
    }

    return null
  }

  private compareInboxRowsByOffHours(a: Row, b: Row): number | null {
    if (a instanceof InboxRow && b instanceof InboxRow) {
      if (a.phoneNumber.isOffHours && !b.phoneNumber.isOffHours) {
        return 1
      }

      if (!a.phoneNumber.isOffHours && b.phoneNumber.isOffHours) {
        return -1
      }
    }

    return null
  }

  private compareIdentityRowsByName(a: Row, b: Row): number | null {
    if (
      a instanceof IdentityRow &&
      b instanceof IdentityRow &&
      a.identity &&
      b.identity
    ) {
      return compareByName(a.identity, b.identity)
    }

    return null
  }

  private compareRowsById(a: Row, b: Row): number {
    return compareString(a.id, b.id)
  }

  private filterRow(row: Row): boolean {
    if (AddNumberRow.is(row)) {
      if (row.isEmergencyNumber) {
        return true
      }

      // Show the number row only if the user has actually input a valid number.
      return Boolean(row.formattedNumber && isValidNumber(row.formattedNumber))
    } else if (HeaderRow.is(row)) {
      // Show this header only if there are unselected rows for which it represents.
      return this.rows.list.some((r) => r.type === row.rowType && this.filterRow(r))
    } else if (InboxMemberRow.is(row) && this.search) {
      // Hide `InboxMemberRow`s rows (aka "Others in ..." rows) when searching
      return false
    }

    // Hide the row if the row's identity is included in the exclusions array
    if (row.identity && this.exclusions.includes(row.identity) && !row.selection) {
      return false
    }

    // No search query, so let's show this row.
    if (!this.search) {
      return true
    }

    // Filter this row by the search query.
    const nameQuery = this.search.toLowerCase()
    const phoneNumberQuery = this.search.replace(/\D/g, '')
    const shouldSearchForPhones = Boolean(phoneNumberQuery)

    return (
      row.identity?.name.toLowerCase().includes(nameQuery) ||
      (shouldSearchForPhones
        ? row.phones.some((phone) => phone.number.includes(phoneNumberQuery))
        : false)
    )
  }

  tearDown() {
    this.disposeBag.dispose()
  }
}

export class PhoneNumberSelectorKeyboardListController extends KeyboardListController<Row> {
  override readonly itemHeight: number = 30
  readonly itemHoverMode = 'select'
  readonly itemFocusMode = 'select'
  readonly focusItemOnSelect: boolean = false
  override readonly highlighterSpacing = { top: 0, right: 4, bottom: 0, left: 4 }
  readonly stepper: Stepper<Row>

  constructor(protected controller: PhoneNumberSelectorController) {
    super()

    this.stepper = new Stepper<Row>(this, {
      name: `phone-number-selector-${this.controller.id}`,
      skip: HeaderRow.is,
    })

    makeObservable(this, {
      data: computed,
      handleEnter: action.bound,
    })
  }

  get data() {
    return this.controller.data.list
  }

  override handleEnter(row: Row) {
    const rowToSelect = row ?? this.data.find((r) => r.type === 'add-number')
    if (rowToSelect) {
      this.controller.handleSelectRow(rowToSelect)
    }
  }
}

export type IdentityRowInstance =
  | InboxMemberRow
  | InboxRow
  | MemberRow
  | ContactRow
  | NumberRow
  | AddNumberRow

type IdentityRowInstanceType = IdentityRowInstance['type']

type IdentityRowClass =
  | typeof InboxMemberRow
  | typeof InboxRow
  | typeof MemberRow
  | typeof ContactRow
  | typeof NumberRow
  | typeof AddNumberRow

export type Row = AddNumberRow | HeaderRow | IdentityRowInstance
type RowType = Row['type']

abstract class BaseRow {
  abstract id: string
  abstract type: RowType

  selection: DatedPhoneNumberSelection | null = null

  constructor(protected controller: PhoneNumberSelectorController) {
    makeObservable(this, {
      selection: observable.ref,
    })

    reaction(
      () => this.selection,
      () => {
        this.controller.debug('selection changed (row: %O)', this)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- FIXME: Fix this ESLint violation!
        this.controller.data.put(this as any)
        this.controller.data.refilter()
      },
      { name: 'Row.SelectionChange' },
    )
  }

  abstract matches(selection: PhoneNumberSelection): boolean
}

interface IdentityPhoneMeta {
  disabled?: boolean
  tooltipDisabledReason?: string
  labelDisabledReason?: string
}

interface RowMeta {
  disabled?: boolean
  tooltipDisabledReason?: string
  labelDisabledReason?: string
}

export abstract class IdentityRow extends BaseRow {
  static getRowClass(type: IdentityRowInstanceType): IdentityRowClass {
    switch (type) {
      case InboxMemberRow.type:
        return InboxMemberRow
      case InboxRow.type:
        return InboxRow
      case MemberRow.type:
        return MemberRow
      case ContactRow.type:
        return ContactRow
      case NumberRow.type:
        return NumberRow
      case AddNumberRow.type:
        return AddNumberRow
    }
  }

  abstract override type: IdentityRowInstanceType
  abstract identity: Identity | null

  readonly showPhoneNumbers: boolean = true
  readonly rowMeta: Map<string, RowMeta> = new Map()
  readonly phoneNumberMeta: Map<string, IdentityPhoneMeta> = new Map()

  constructor(controller: PhoneNumberSelectorController) {
    super(controller)
    makeObservable(this, {
      rowMeta: observable,
      phoneNumberMeta: observable,
      phones: computed,
      sharedPhones: computed,
      privatePhones: computed,
    })
  }

  abstract handleSelect(): void

  get phones(): IdentityPhone[] {
    return this.identity?.phones ?? []
  }

  get sharedPhones(): IdentityPhone[] {
    return this.phones.filter((p) => p.isShared)
  }

  get privatePhones(): IdentityPhone[] {
    return this.phones.filter((p) => !p.isShared)
  }

  get isOffHours(): boolean {
    return (this.phones.length === 1 && this.phones[0]?.isOffHours) ?? false
  }
}

export class AddNumberRow extends IdentityRow {
  static readonly type = 'add-number'

  static is(row?: Row | null): row is AddNumberRow {
    return row?.type === AddNumberRow.type
  }

  readonly type = AddNumberRow.type
  readonly id: string

  rawNumber: string | null = null

  constructor(controller: PhoneNumberSelectorController) {
    super(controller)

    this.id = this.type

    makeObservable(this, {
      rawNumber: observable.ref,
      handleSelect: action.bound,
      matches: action.bound,
    })
  }

  get formattedNumber(): string | null {
    if (this.isEmergencyNumber) {
      return this.rawNumber
    }

    return this.rawNumber ? formatPartial(this.rawNumber) : null
  }

  get identity(): Identity | null {
    return this.formattedNumber
      ? createAnonymousIdentityFromNumber(this.formattedNumber)
      : null
  }

  get isEmergencyNumber(): boolean {
    return this.rawNumber ? emergencyNumbers.includes(this.rawNumber) : false
  }

  handleSelect() {
    this.controller.handleSelectRow(this)
  }

  matches(selection: PhoneNumberSelection) {
    return selection.type === this.type && selection.to === this.formattedNumber
  }
}

class HeaderRow extends BaseRow {
  static readonly type = 'header'

  static is(row?: Row | null): row is HeaderRow {
    return row?.type === HeaderRow.type
  }

  readonly type = HeaderRow.type

  constructor(
    controller: PhoneNumberSelectorController,
    readonly rowType: Exclude<RowType, 'header'>,
    readonly title: string,
  ) {
    super(controller)
    makeObservable(this, {
      matches: action.bound,
    })
  }

  get id() {
    return `${this.rowType}-${this.type}`
  }

  matches() {
    return false
  }
}

class NumberRow extends IdentityRow {
  static readonly type = 'number'

  static is(row?: Row | null): row is NumberRow {
    return row?.type === NumberRow.type
  }

  override readonly showPhoneNumbers = false

  readonly type = NumberRow.type
  readonly id: string

  constructor(
    controller: PhoneNumberSelectorController,
    selection: DatedPhoneNumberSelection,
  ) {
    super(controller)
    this.selection = selection
    this.id = `${this.type}-${this.number}`
    makeObservable(this, {
      identity: computed,
      handleSelect: action.bound,
      matches: action.bound,
    })
  }

  get number() {
    return this.selection?.type === NumberRow.type ? this.selection.to : null
  }

  get identity(): Identity | null {
    return this.number ? createAnonymousIdentityFromNumber(this.number) : null
  }

  handleSelect() {
    this.controller.handleSelectRow(this)
  }

  matches(selection: PhoneNumberSelection) {
    return (
      selection.type === this.type &&
      toE164(selection.to) === this.identity?.phones[0]?.number
    )
  }
}

class InboxMemberRow extends IdentityRow {
  static readonly type = 'inbox-member'

  static is(row?: Row | null): row is InboxMemberRow {
    return row?.type === InboxMemberRow.type
  }

  override readonly showPhoneNumbers = false

  readonly type = InboxMemberRow.type
  readonly id: string

  constructor(
    controller: PhoneNumberSelectorController,
    readonly member: MemberModel,
    readonly phoneNumber: PhoneNumberModel,
  ) {
    super(controller)
    this.id = `${this.type}-${this.member.id}`
    makeObservable(this, {
      identity: computed,
      handleSelect: action.bound,
      matches: action.bound,
    })
  }

  get identity() {
    return this.member
  }

  handleSelect() {
    this.controller.handleSelectRow(this)
  }

  matches(selection: PhoneNumberSelection) {
    return selection.type === this.type && selection.to === this.member
  }

  override get isOffHours(): boolean {
    return this.identity.isOffHours
  }
}

export class InboxRow extends IdentityRow {
  static readonly type = 'inbox'

  static is(row?: Row | null): row is InboxRow {
    return row?.type === InboxRow.type
  }

  override readonly showPhoneNumbers = false

  readonly type = InboxRow.type
  readonly id: string

  constructor(
    controller: PhoneNumberSelectorController,
    readonly phoneNumber: PhoneNumberModel,
  ) {
    super(controller)
    this.id = `${this.type}-${this.phoneNumber.id}`
    makeObservable(this, {
      identity: computed,
      handleSelect: action.bound,
      matches: action.bound,
    })
  }

  get identity() {
    return this.phoneNumber.group
  }

  handleSelect() {
    this.controller.handleSelectRow(this)
  }

  matches(selection: PhoneNumberSelection) {
    return selection.type === this.type && selection.to === this.phoneNumber
  }
}

export class MemberRow extends IdentityRow {
  static readonly type = 'member'

  static is(row?: Row | null): row is MemberRow {
    return row?.type === MemberRow.type
  }

  readonly type = MemberRow.type
  readonly id: string
  override readonly showPhoneNumbers = true

  constructor(
    controller: PhoneNumberSelectorController,
    readonly member: MemberModel,
  ) {
    super(controller)
    this.id = `${this.type}-${this.member.id}`
    makeObservable(this, {
      identity: computed,
      handleSelect: action.bound,
      matches: action.bound,
    })
  }

  get identity() {
    return this.member
  }

  handleSelect() {
    this.controller.handleSelectRow(this)
  }

  matches(selection: PhoneNumberSelection) {
    return selection.type === this.type && selection.to === this.member
  }

  override get isOffHours(): boolean {
    return this.identity.isOffHours
  }
}

export class ContactRow extends IdentityRow {
  static readonly type = 'contact'

  static is(row?: Row | null): row is ContactRow {
    return row?.type === ContactRow.type
  }

  readonly type = ContactRow.type
  readonly id: string

  constructor(
    controller: PhoneNumberSelectorController,
    readonly contact: ContactModel,
  ) {
    super(controller)
    this.id = `${this.type}-${this.contact.id}`
    makeObservable(this, {
      identity: computed,
      handleSelect: action.bound,
      matches: action.bound,
    })
  }

  get identity() {
    return this.contact
  }

  handleSelect() {
    this.controller.handleSelectRow(this)
  }

  matches(selection: PhoneNumberSelection) {
    return selection.type === this.type && selection.to === this.contact
  }
}
